// @ts-strict-ignore
import AnalyticsApi from '../api/AnalyticsApi';
import Cookie from '../shared/Cookie';
import { getIOSInstallationID } from '../shared/urlParams';

export type EventSourceContext = {
  error?: unknown;
  errorInfo?: string;
  eventSource?: string;
  eventAction?: string;
  eventSourceName?: string;
  eventSourceId?: string;
  eventTime: Date;
  eventName: string;
};

type CustomAttributes = {
  [key: string]: string | number | Error;
};

export type ClickStreamEvent = {
  visitorContext: {
    browserCookieId: string;
    userId: string;
    userAgent: string;
    clientIp: string;
  };
  browserContext: BrowserData;
  eventSourceContext: EventSourceContext;
  customAttributes: CustomAttributes;
};

export type CarouselEventData = {
  userInteracted: boolean;
  reachedEnd: boolean;
  totalSlides: number;
  visibleSlides: number;
};

type BrowserData = {
  referrerUrl: string;
  url: string;
  browserName: string;
  osName: string;
  deviceCategory: string | null;
  deviceName: string | null;
  language: string;
  screenResolution: string;
  browserResolution: string;
};

enum EventActions {
  Changed = 'changed',
  Clicked = 'clicked',
  Visited = 'page_visited',
  Scrolled = 'scrolled',
  Success = 'success'
}

enum EventSources {
  Redux = 'system',
  UserInteraction = 'user_interaction'
}

enum EventNames {
  Changed = 'Changed',
  Clicked = 'Clicked',
  Visited = 'Visited'
}

const getOsName = () => {
  const appVersion = window?.navigator?.appVersion || '';

  if (appVersion.indexOf('Win') !== -1) {
    return 'Windows';
  }
  if (appVersion.indexOf('iPhone OS') !== -1) {
    return 'iPhone OS';
  }
  if (appVersion.indexOf('Mac') !== -1) {
    return 'MacOS';
  }
  if (appVersion.indexOf('X11') !== -1) {
    return 'UNIX';
  }
  if (appVersion.indexOf('Linux') !== -1) {
    return 'Linux';
  }
  return `${window?.navigator?.platform || 'Unknown OS'}`;
};

const getBrowserName = () => {
  const useragent = window?.navigator?.userAgent ?? '';
  let matchedName =
    useragent.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
  if (/trident/i.test(matchedName[1])) {
    const ieRevision = /\brv[ :]+(\d+)/g.exec(useragent) || [];
    return `IE ${ieRevision[1] || ''}`;
  }
  if (matchedName[1] === 'Chrome') {
    const operaRevision = useragent.match(/\bOPR\/(\d+)/);
    if (operaRevision !== null) {
      return `Opera ${operaRevision[1]}`;
    }
    const edgeRevision = useragent.match(/\bEdg\/(\d+)/);
    if (edgeRevision !== null) {
      return `Edge ${edgeRevision[1]}`;
    }
  }
  matchedName = matchedName[2]
    ? [matchedName[1], matchedName[2]]
    : [window?.navigator.appName, window?.navigator?.appVersion, '-?'];
  const revision = useragent.match(/version\/(\d+)/i);
  if (revision !== null) {
    matchedName.splice(1, 1, revision[1]);
  }

  return matchedName.join(' ');
};

const getBrowserData = (): BrowserData => ({
  browserName: getBrowserName(),
  osName: getOsName(),
  browserResolution: `${window?.innerWidth}x${window?.innerHeight}`,
  screenResolution: `${window?.screen?.width}x${window?.screen?.height}`,

  deviceCategory: window?.WURFL?.complete_device_name ?? null,
  deviceName: window?.WURFL?.form_factor ?? null,

  language: window?.navigator?.language,
  referrerUrl: window?.document?.referrer,
  url: window?.location?.href
});

const makeEventName = (eventName: string) => `ValueTracker - ${eventName}`;

const getIpAddress = () => Cookie.getCookie('CVCurrentIpAddress') || '';


const makeEvent = (
  eventSourceContext: EventSourceContext,
  customAttributes: CustomAttributes = {}
): ClickStreamEvent => {
  const iosInstallationID = getIOSInstallationID();
  
  // Order of spreading matters - iosInstallationID LAST ensures session storage value has priority
  const mergedAttributes = {
    ...customAttributes,
    ...iosInstallationID
  };
  
  return {
    visitorContext: {
      browserCookieId: Cookie.getCookie('BrowserCookieId'),
      userId: Cookie.getUserId() || null,
      userAgent: window?.navigator?.userAgent,
      clientIp: getIpAddress()
    },
    browserContext: getBrowserData(),
    eventSourceContext,
    customAttributes: mergedAttributes
  };
};

const recordMoMetricsEvent = (
  eventSourceContext: EventSourceContext,
  customAttributes: CustomAttributes = {},
  sendImmediately = false
): ClickStreamEvent | Promise<unknown> | void => {
  const event = makeEvent(eventSourceContext, customAttributes);
  if (sendImmediately) {
    return AnalyticsApi.recordNow(event);
  }

  AnalyticsApi.queue(event);
  return event;
};

const makeEventNameAndTime = (
  eventName: string,
  details: Partial<EventSourceContext>
): EventSourceContext => ({
  eventName: makeEventName(eventName),
  eventTime: new Date(),
  ...details
});

const recordActionClicked = (
  pageName: string,
  component: string,
  eventClickedData = {},
  sendImmediately?: boolean
) => {
  const eventName = component ? `${pageName}:${component}` : pageName;
  return recordMoMetricsEvent(
    makeEventNameAndTime(`${eventName} - ${EventNames.Clicked}`, {
      eventAction: EventActions.Clicked,
      eventSourceName: component ? `Component:${component}` : `Page:${pageName}`,
      eventSource: EventSources.UserInteraction
    }),
    eventClickedData,
    sendImmediately
  );
};

const recordGenericPageEvent = (
  pageName: string,
  component: string,
  eventAction: EventActions,
  eventClickedData = {},
  sendImmediately?: boolean
) => {
  const eventName = component ? `${pageName}:${component}` : pageName;
  recordMoMetricsEvent(
    makeEventNameAndTime(`${eventName} - ${eventAction}`, {
      eventAction,
      eventSourceName: component ? `Component:${component}` : `Page:${pageName}`,
      eventSource: EventSources.UserInteraction
    }),
    eventClickedData,
    sendImmediately
  );
};

const recordGenericEvent = (
  eventName: string,
  eventContext = {},
  sendImmediately?: boolean
) => {
  recordMoMetricsEvent(
    makeEventNameAndTime(eventName, {
      eventSource: EventSources.UserInteraction
    }),
    eventContext,
    sendImmediately
  );
};

const recordEventAndNavigate = async (
  event: { currentTarget: { href: string }; preventDefault: () => void },
  pageName: string,
  component: string,
  eventClickedData = {}
) => {
  event.preventDefault();
  const { href } = event.currentTarget;
  await recordActionClicked(pageName, component, eventClickedData, true);
  window.location.href = href;
};

const recordEventAndNavigateTo = async (
  destination: string,
  pageName: string,
  component: string,
  eventClickedData = {}
) => {
  await recordActionClicked(pageName, component, eventClickedData, true);
  window.location.href = destination;
};

const recordEventAndNavigateInNewWindow = async (
  destination: string,
  pageName: string,
  component: string,
  eventClickedData = {}
) => {
  await recordActionClicked(pageName, component, eventClickedData, true);
  const newWindow = window.open('', '_blank');
  if (newWindow) {
    newWindow.opener = null;
    newWindow.location = destination;
  }
};

const recordError = (error: unknown, errorInfo: string) =>
  recordMoMetricsEvent(
    makeEventNameAndTime(`Error`, {
      eventSourceName: 'Component:ErrorBoundary',
      eventSource: 'JavascriptError',
      error,
      errorInfo
    })
  );

export {
  EventActions,
  EventSources,
  makeEvent,
  makeEventName,
  makeEventNameAndTime,
  recordActionClicked,
  recordError,
  recordEventAndNavigate,
  recordEventAndNavigateTo,
  recordEventAndNavigateInNewWindow,
  recordGenericEvent,
  recordGenericPageEvent,
  recordMoMetricsEvent
};
